import * as React from 'react';
import { Formik, Form } from 'formik';
import styled, { css } from 'styled-components';

import type { DateTime, DateTimeFormats } from 'util/DateTime';
import ArchiveSizeEstimate from 'data-lake/components/ArchiveSizeEstimate';
import { Modal, Input } from 'components/bootstrap';
import { FormikInput, ModalSubmit } from 'components/common';
import useUserDateTime from 'hooks/useUserDateTime';
import type { AbsoluteTimeRange } from 'views/logic/queries/Query';
import type { DataLakeActionFormValues, DataLakeActionValues } from 'data-lake/Types';
import { getRangeValidationErrors } from 'data-lake/components/DataLakeActionModals/utils';
import TimeRangeField from 'data-lake/components/DataLakeActionModals/TimeRangeField';

export type Props = {
  defaultTimeRange?: AbsoluteTimeRange;
  show: boolean;
  onHide: () => void;
  children: React.ReactNode;
  onSubmit: (values: DataLakeActionValues) => void;
  dataLakeId: string;
  streamIds: Array<string>;
};
type FormatTime = (time: DateTime, format?: DateTimeFormats) => string;

const StyledModal = styled(Modal)`
  z-index: 1070;
`;

const FullDelete = styled.div(
  ({ theme }) => css`
    display: flex;
    gap: ${theme.spacings.lg};
    margin-top: ${theme.spacings.lg};
  `,
);

const getDefaultValues = (formatTime: FormatTime, timeRange?: AbsoluteTimeRange) => {
  const from = timeRange?.from || new Date().setMinutes(0, 0);
  const to = timeRange?.to || new Date().setMinutes(0, 0);

  return {
    nextTimeRange: {
      type: 'absolute' as const,
      from: formatTime(from),
      to: formatTime(to),
    },
  };
};

const submitButtonText = 'Delete';

const DataLakeDeleteActionForm = ({
  defaultTimeRange = null,
  show,
  onSubmit,
  onHide,
  children,
  dataLakeId,
  streamIds,
}: Props) => {
  const { formatTime } = useUserDateTime();

  const validate = (values: DataLakeActionFormValues) => {
    const {
      nextTimeRange: { to, from },
      fullDelete,
    } = values;
    const errors: {
      nextTimeRange?: {
        from?: string;
        to?: string;
      };
    } = !fullDelete ? getRangeValidationErrors(from, to) : {};

    return errors;
  };

  const handleSubmit = ({
    nextTimeRange: { from, to },
    fullDelete,
    wipeRestores,
    streamIds: streamIdList,
  }: DataLakeActionFormValues) => {
    onSubmit({
      from,
      to,
      full_delete: fullDelete,
      wipe_restores: wipeRestores,
      data_lake_id: dataLakeId,
      stream_ids: streamIdList,
    });
  };

  return (
    <StyledModal onHide={() => onHide()} show={show}>
      <Formik
        initialValues={{
          ...getDefaultValues(formatTime, defaultTimeRange),
        }}
        onSubmit={handleSubmit}
        validate={validate}
        validateOnMount>
        {({ isSubmitting, isValidating, isValid, values: { fullDelete, nextTimeRange } }) => (
          <Form>
            <Modal.Header>
              <Modal.Title>Deleting Data Lake data! Are you sure?</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {children}
              <FullDelete>
                <Input id="fullDelete" label="Full Delete:">
                  <FormikInput
                    label="Delete all data in the Data Lake"
                    name="fullDelete"
                    id="fullDelete"
                    type="checkbox"
                  />
                </Input>
                {fullDelete && (
                  <Input id="wipe_restores" label="Wipe retrievals:">
                    <FormikInput
                      label="Wipe all retrievals"
                      name="wipeRestores"
                      id="wipeRestores"
                      disabled={!fullDelete}
                      type="checkbox"
                    />
                  </Input>
                )}
              </FullDelete>
              {!fullDelete && <TimeRangeField submitButtonText={submitButtonText} hourOnly />}
              {!fullDelete && isValid && (
                <ArchiveSizeEstimate
                  estimateParams={{
                    streamIds,
                    dataLakeId,
                    from: nextTimeRange.from,
                    to: nextTimeRange.to,
                  }}
                />
              )}
            </Modal.Body>
            <Modal.Footer>
              <ModalSubmit
                submitButtonText={submitButtonText}
                submitLoadingText="Submitting request..."
                onCancel={() => onHide()}
                disabledSubmit={isValidating || !isValid}
                isSubmitting={isSubmitting}
              />
            </Modal.Footer>
          </Form>
        )}
      </Formik>
    </StyledModal>
  );
};

export default DataLakeDeleteActionForm;
